import type { ScoreReading } from '@/features/care/scoreSeries'
import type { VisitRecord } from '@/features/care/visits'

/**
 * The visits a member has had — empty, because nothing serves them yet.
 *
 * `/appointments/patient-upcoming` returns the future only, and `/appointment-results/latest`
 * returns one result with no date, no visit name and no studio on it. So a member's own history is
 * unreachable from the app today, and `VisitsSection` draws nothing rather than an empty table.
 *
 * This exists as the seam rather than as a stub: Care and the record page both read their visits
 * through it, so the day provider-kapi's history endpoint lands, this file becomes a `useQuery`
 * and nothing that calls it changes. What that endpoint has to return is `VisitRecord` — every
 * visit that has happened, with its results where they have been released.
 */
export const useVisitRecords = (): { visits: VisitRecord[]; isLoading: boolean } => ({
	visits: [],
	isLoading: false,
})

/** The chart's readings, which are the visits that have a score, said in the chart's own terms. */
export const readingsOf = (visits: VisitRecord[]): ScoreReading[] =>
	visits
		.filter((visit) => visit.results)
		.map((visit) => ({
			appointmentId: visit.appointmentId,
			on: visit.on,
			score: visit.results?.score ?? 0,
		}))
